import React, { useState } from 'react';
import { Card, CardContent, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const SummarySection = ({ summary }) => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(null);

  const handleTooltipToggle = (label) => {
    setTooltipOpen((prev) => (prev === label ? null : label));
  };

  const pendingStatuses = [
    'BOOKING',
    'PICKED',
    'PENDING',
    'ON_DELIVERY',
    'DELIVERED_BY_RIDER',
    'RETURNED_BY_RIDER',
    'PARTIALLY_DELIVERED_BY_RIDER',
  ];

  const deliveredStatuses = ['PARTIALLY_DELIVERED', 'DELIVERED'];

  const returnStatuses = ['RETURNED', 'CUSTOMER_RETURN'];

  const getStatusesForLabel = (label) => {
    switch (label) {
      case 'Pending':
        return pendingStatuses;
      case 'Delivered':
        return deliveredStatuses;
      case 'Return':
        return returnStatuses;
      case 'Total':
        return [...pendingStatuses, ...deliveredStatuses, ...returnStatuses];
      default:
        return [];
    }
  };

  const pendingCount = pendingStatuses.reduce((total, status) => total + (summary[status]?.count || 0), 0);
  const deliveredCount = deliveredStatuses.reduce((total, status) => total + (summary[status]?.count || 0), 0);
  const returnCount = returnStatuses.reduce((total, status) => total + (summary[status]?.count || 0), 0);
  const totalCount = pendingCount + deliveredCount + returnCount;

  const pendingAmount = pendingStatuses.reduce((total, status) => total + (summary[status]?.amount || 0), 0);
  const deliveredAmount = deliveredStatuses.reduce((total, status) => total + (summary[status]?.amount || 0), 0);
  const returnAmount = returnStatuses.reduce((total, status) => total + (summary[status]?.amount || 0), 0);
  const totalAmount = pendingAmount + deliveredAmount + returnAmount;

  const statuses = [
    { label: 'Pending', count: pendingCount, amount: pendingAmount },
    { label: 'Delivered', count: deliveredCount, amount: deliveredAmount },
    { label: 'Return', count: returnCount, amount: returnAmount },
    { label: 'Total', count: totalCount, amount: totalAmount },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 p-1">
      {statuses.map((status) => (
        <Card
          key={status.label}
          className="shadow-md rounded-md bg-gradient-to-b from-white to-gray-100 hover:shadow-lg transition-shadow duration-200 relative"
          onMouseEnter={() => setHoveredCard(status.label)}
          onMouseLeave={() => setHoveredCard(null)}
        >
          {hoveredCard === status.label && (
            <div
              className="absolute top-2 right-2 text-gray-500 cursor-pointer"
              onClick={() => handleTooltipToggle(status.label)}
            >
              <Tooltip
                title={`Count: ${status.count}, Amount: ${status.amount || 'N/A'}, Statuses: ${getStatusesForLabel(
                  status.label,
                ).join(', ')}`}
                open={tooltipOpen === status.label}
                arrow
              >
                <InfoOutlinedIcon fontSize="small" />
              </Tooltip>
            </div>
          )}
          <CardContent className="flex flex-col items-center text-center p-2">
            <div className="text-2xl mb-1">
              {status.label === 'Pending' && '⏳'}
              {status.label === 'Delivered' && '📦'}
              {status.label === 'Return' && '🔄'}
              {status.label === 'Total' && '📊'}
            </div>
            <Typography variant="h6" className="font-bold text-sm mb-1">
              {status.label} {status.count}
            </Typography>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
              <div
                className="bg-blue-500 h-2 rounded-full"
                style={{ width: `${(status.count / totalCount) * 100}%` }}
              ></div>
            </div>
            <Typography variant="body2" className="text-gray-600">
              {status.amount ? `Amount: ${status.amount}` : ''}
            </Typography>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default SummarySection;
